import { useEffect, useMemo } from 'react';
import { NOTE_PHOTO_ACCEPT, NOTE_PHOTO_MAX } from '../note-photos.js';

// "+ Photo" for a journal entry form (and the feedback form's screenshots),
// with a preview of each picked photo and a way to take one back out
// before saving. Only images are kept: a picked PDF or video is left out.
// Most browsers other than Safari cannot draw HEIC, so an iPhone photo
// shows as a HEIC tile; the upload converts it either way.
function isHeic(f) {
  return /image\/(heic|heif)/i.test(f.type || '') || /\.(heic|heif)$/i.test(f.name || '');
}

function isImage(f) {
  return /^image\//.test(f.type || '') || /\.(heic|heif|jpe?g|png|webp|gif)$/i.test(f.name || '');
}

export function NotePhotoPicker({
  files,
  onChange,
  disabled = false,
  max = NOTE_PHOTO_MAX,
  label = '+ Photo',
}) {
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(
    () => () => {
      for (const u of previews) URL.revokeObjectURL(u);
    },
    [previews]
  );
  const full = files.length >= max;

  return (
    <div className="note-photos">
      {previews.map((src, i) => (
        <span key={src} className="note-photos-item">
          {isHeic(files[i]) ? (
            <span className="note-photos-heic font-heading">HEIC</span>
          ) : (
            <img src={src} alt="" />
          )}
          <button
            type="button"
            className="note-photos-x"
            onClick={() => onChange(files.filter((_, j) => j !== i))}
            disabled={disabled}
            aria-label="Remove this photo"
          >
            ×
          </button>
        </span>
      ))}
      {!full && (
        <label
          className="note-photos-add font-heading"
          aria-disabled={disabled}
          style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {label}
          <input
            type="file"
            accept={NOTE_PHOTO_ACCEPT}
            multiple
            style={{ display: 'none' }}
            disabled={disabled}
            onChange={(e) => {
              const picked = Array.from(e.target.files || []).filter(isImage);
              e.target.value = '';
              if (picked.length) onChange([...files, ...picked].slice(0, max));
            }}
          />
        </label>
      )}
    </div>
  );
}
